import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import * as api from "../lib/api.ts";

/**
 * What the app worked out, as opposed to what was typed or prescribed: an
 * analysis, a suggestion, a summary written by Claude. Always the sparkle,
 * always in mauve, so it is never mistaken for the coach's word.
 */
export function Insight({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={[
        "flex items-start gap-2 rounded-[8px] bg-insight/10 px-3 py-2 text-[0.85rem] leading-snug text-ink-soft ring-1 ring-insight/20",
        className,
      ].join(" ")}
    >
      <Sparkles size={14} strokeWidth={2.25} className="mt-[2px] shrink-0 text-insight" aria-hidden />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** A card's label when the card itself is the app's reading: the summary of a session, a cycle's report. */
export function InsightLabel({ children }: { children: ReactNode }) {
  return (
    <p className="label flex items-center gap-1.5 text-insight">
      <Sparkles size={12} strokeWidth={2.5} aria-hidden />
      {children}
    </p>
  );
}

/**
 * What the server has to say for a screen, one line each; nothing at all
 * when it has nothing to say, or cannot be reached — a reading is never
 * worth an error in the middle of a session.
 */
export function Insights({ scope, className = "" }: { scope: string; className?: string }) {
  const insights = useQuery({ queryKey: ["insights", scope], queryFn: () => api.getInsights(scope), retry: false });
  const lines = insights.data?.insights ?? [];
  if (lines.length === 0) return null;
  return (
    <div className={["space-y-2", className].join(" ")}>
      {lines.map((insight) => (
        <Insight key={insight.kind}>{insight.text}</Insight>
      ))}
    </div>
  );
}
